import {
  ETAPAS_PEDIDO,
  ROTULO_ETAPA,
  type AtendenteDTO,
  type ConversaDetalheDTO,
  type EtapaPedido,
} from '@viniko/shared';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Modal, useToast } from '../../componentes/ui';
import { patch, post } from '../../lib/api';
import { chaves } from '../../lib/consultas';

const diaMes = new Intl.DateTimeFormat('pt-BR', { day: '2-digit', month: '2-digit' });

const PROXIMA_ACAO: Partial<Record<EtapaPedido, string>> = {
  orcamento: 'Cliente aprovou o orçamento',
  pagamento: 'Confirmar pagamento',
  arte: 'Arte enviada ao cliente',
  aprovacao: 'Cliente aprovou a arte',
  producao: 'Pedido pronto',
  pronto: 'Pedido enviado',
  enviado: 'Pedido entregue',
};

export function Pedido({ conversa: c, eu }: { conversa: ConversaDetalheDTO; eu: AtendenteDTO }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [confirmar, setConfirmar] = useState<EtapaPedido | null>(null);
  const [salvando, setSalvando] = useState(false);
  const podeMudar =
    eu.papel === 'admin' || (c.status === 'atendimento' && c.atendente?.id === eu.id);
  const pedido = c.pedido;

  const atualizar = () => {
    qc.invalidateQueries({ queryKey: chaves.conversa(c.id) });
    qc.invalidateQueries({ queryKey: ['conversas'] });
  };

  async function mudar(etapa: EtapaPedido) {
    if (!pedido) return;
    setSalvando(true);
    try {
      await patch(`/api/pedidos/${pedido.id}/etapa`, { etapa, conversaId: c.id });
      toast(
        etapa === 'arte' && pedido.etapa === 'pagamento'
          ? 'Pagamento confirmado'
          : `Pedido em ${ROTULO_ETAPA[etapa]}`,
      );
      atualizar();
      setConfirmar(null);
    } catch (e) {
      toast((e as Error).message, 'erro');
    } finally {
      setSalvando(false);
    }
  }

  async function criar() {
    try {
      await post('/api/pedidos', { conversaId: c.id });
      toast('Pedido aberto');
      atualizar();
    } catch (e) {
      toast((e as Error).message, 'erro');
    }
  }

  if (!pedido || pedido.etapa === 'entregue') {
    return (
      <div className="pedido-vazio">
        {pedido?.etapa === 'entregue' ? (
          <p className="nota-sec">Último pedido entregue. Nenhum pedido em andamento.</p>
        ) : (
          <p className="nota-sec">Nenhum pedido aberto para este cliente.</p>
        )}
        {podeMudar && (
          <button className="btn" onClick={criar}>
            Abrir pedido
          </button>
        )}
      </div>
    );
  }

  const atual = ETAPAS_PEDIDO.indexOf(pedido.etapa);
  const proxima = ETAPAS_PEDIDO[atual + 1];
  const etapas = ETAPAS_PEDIDO.filter(
    (e) => e !== 'pronto' || atual >= ETAPAS_PEDIDO.indexOf('pronto'),
  );
  const quando = new Map<EtapaPedido, string>(pedido.historico.map((h) => [h.etapa, h.em]));

  return (
    <>
      <ol className="etapas">
        {etapas.map((e) => {
          const i = ETAPAS_PEDIDO.indexOf(e);
          const estado = i < atual ? 'feita' : i === atual ? 'atual' : 'futura';
          const em = quando.get(e);
          return (
            <li key={e} className={estado} aria-current={estado === 'atual' ? 'step' : undefined}>
              {podeMudar && e !== pedido.etapa ? (
                <button
                  className="etapa-nome"
                  title={`Mudar o pedido para ${ROTULO_ETAPA[e]}`}
                  onClick={() => setConfirmar(e)}
                >
                  {ROTULO_ETAPA[e]}
                </button>
              ) : (
                <span className="etapa-nome">{ROTULO_ETAPA[e]}</span>
              )}
              {em && estado !== 'futura' && (
                <span className="etapa-data">{diaMes.format(new Date(em))}</span>
              )}
            </li>
          );
        })}
      </ol>

      {podeMudar && proxima && (
        <div className="pedido-acoes">
          <button className="btn primario" disabled={salvando} onClick={() => void mudar(proxima)}>
            {PROXIMA_ACAO[pedido.etapa] ?? `Avançar para ${ROTULO_ETAPA[proxima]}`}
          </button>
          <small>Ou clique numa etapa para corrigir.</small>
        </div>
      )}
      {!podeMudar && <p className="nota-sec">Só quem está atendendo a conversa muda o pedido.</p>}

      {confirmar && (
        <Modal titulo="Mudar etapa do pedido" aoFechar={() => setConfirmar(null)}>
          <p style={{ margin: 0 }}>
            Mudar de <b>{ROTULO_ETAPA[pedido.etapa]}</b> para <b>{ROTULO_ETAPA[confirmar]}</b>?
            {ETAPAS_PEDIDO.indexOf(confirmar) < atual && ' O pedido vai voltar uma etapa.'}
            {confirmar === 'entregue' && ' A conversa será finalizada.'}
          </p>
          <div className="acoes">
            <button className="btn" onClick={() => setConfirmar(null)}>
              Cancelar
            </button>
            <button
              className="btn primario"
              disabled={salvando}
              onClick={() => void mudar(confirmar)}
            >
              Mudar para {ROTULO_ETAPA[confirmar]}
            </button>
          </div>
        </Modal>
      )}
    </>
  );
}
